<template>
  <NModal
    :bordered="false"
    class="cb-data-charts-settings"
    :onEsc="onCancel"
    preset="card"
    :show="true"
    size="huge"
    style="max-width: min(1200px, 90vw); max-height: 90vh"
    :style="{
      '--n-border-radius': '16px',
      '--n-padding-top': '16px',
      '--n-padding-right': '16px',
      '--n-padding-bottom': '16px',
      '--n-padding-left': '16px',
    }"
    :title="t('canvasUI.canvasBuilder.dataCharts.editDataChartsComponent')"
    @close="onCancel"
  >
    <NCard
      aria-modal="true"
      :bordered="false"
      class="flex"
      embedded
      role="dialog"
      :style="{
        '--n-color-embedded-modal': themeVars.base,
        '--n-padding-top': 0,
        '--n-padding-right': 0,
        '--n-padding-bottom': 0,
        '--n-padding-left': 0,
      }"
    >
      <div class="flex gap-4 h-[calc(80vh-120px)] w-full">
        <!-- Settings Panel -->
        <div class="cb-data-charts-settings__card overflow-auto flex-1">
          <div class="flex flex-col h-full">
            <div class="flex-grow overflow-y-auto overflow-x-hidden">
              <!-- Data Settings Section -->
              <div class="text-sm font-semibold mb-3 text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                {{ t('canvasUI.canvasBuilder.dataCharts.dataSettings') }}
              </div>

              <!-- Series Tabs (Browser-style) -->
              <div class="flex items-end border-b border-gray-200 dark:border-gray-700 mb-4 -mx-4 px-4">
                <div
                  v-for="(series, index) in seriesList"
                  :key="series.id"
                  class="series-tab flex items-center gap-1 px-3 py-2 h-9 cursor-pointer border border-b-0 rounded-t-lg mr-1 transition-colors"
                  :class="[
                    activeSeriesIndex === index
                      ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 -mb-px'
                      : 'bg-gray-100 dark:bg-gray-900 border-transparent hover:bg-gray-200 dark:hover:bg-gray-700',
                  ]"
                  @click="activeSeriesIndex = index"
                >
                  <span class="text-sm font-medium truncate max-w-24">{{ series.name }}</span>
                  <div
                    v-if="index > 0"
                    class="ml-1 p-0.5 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                    @click.stop="removeSeries(index)"
                  >
                    <CIcon class="text-gray-500" icon="times" :size="10" />
                  </div>
                </div>
                <button
                  v-if="canAddSeries"
                  class="flex items-center justify-center w-8 h-8 mb-1 rounded bg-transparent border-none hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                  :title="t('canvasUI.canvasBuilder.dataCharts.addSeries')"
                  @click="addSeries"
                >
                  <CIcon icon="plus" :size="14" />
                </button>
              </div>

              <!-- Series Name (only show when multiple series) -->
              <div v-if="seriesList.length > 1" class="mb-4">
                <label class="block mb-2">{{ t('canvasUI.canvasBuilder.dataCharts.seriesName') }}</label>
                <NInput v-model:value="activeSeries.name" type="text" />
              </div>

              <!-- Chart Type (per series) -->
              <div class="mb-4">
                <label class="block mb-2">{{ t('canvasUI.canvasBuilder.dataCharts.chartType') }}</label>
                <NSelect
                  v-model:value="activeSeries.chartType"
                  :options="availableChartTypeOptions"
                  @update:value="onChartTypeChange"
                />
              </div>

              <!-- Data Points -->
              <div class="mb-4">
                <div class="flex justify-between items-center mb-2">
                  <label class="block">{{ t('canvasUI.canvasBuilder.dataCharts.dataPoints') }}</label>
                  <NButton :disabled="!activeSeries.dataPoints.length" size="small" @click="removeAllDataPoints">
                    {{ t('canvasUI.canvasBuilder.dataCharts.removeAll') }}
                  </NButton>
                </div>

                <!-- Regular data points (label + value) for non-scatter/bubble charts -->
                <template v-if="!isActiveSeriesXYChart && !isActiveSeriesXYRChart">
                  <div v-for="(point, index) in activeSeries.dataPoints" :key="index" class="flex gap-2 mb-2">
                    <NInput v-model:value="point.label" class="flex-1" placeholder="Label" />
                    <NInput v-model:value="point.value" class="flex-1" placeholder="Value" type="text">
                      <template v-slot:suffix>
                        <NPopover
                          placement="bottom"
                          :show="activePopoverPoint === point && activePopoverField === 'value'"
                          trigger="manual"
                          @clickoutside="closePopover"
                        >
                          <template #trigger>
                            <div
                              class="cursor-pointer flex items-center"
                              @click="handleDynamicValueClick(point, 'value')"
                            >
                              <CIcon :class="point.dynamicValue ? 'text-blue-500' : ''" icon="caret-down" />
                            </div>
                          </template>
                          <div class="p-4 min-w-[400px] max-h-[400px]">
                            <div class="flex gap-4 h-full">
                              <div class="flex-1 flex flex-col">
                                <div class="text-sm mb-2">
                                  {{ t('canvasUI.canvasBuilder.dataCharts.selectDynamicValue') }}
                                </div>
                                <div class="overflow-auto">
                                  <NTree
                                    block-line
                                    class="max-h-[300px]"
                                    :data="dynamicValueTreeData"
                                    :render-label="renderDynamicValueTreeLabel"
                                    :selected-keys="[point.dynamicValue?.path ?? '']"
                                    @update:selected-keys="(keys) => handleDynamicValueSelect(keys, point, 'value')"
                                  />
                                </div>
                              </div>
                              <!-- Field Selection for Arrays/Objects -->
                              <template
                                v-if="point.selectedValue && point.arrayConfig && isArrayOrObject(point.selectedValue)"
                              >
                                <div class="flex-1 border-l pl-4">
                                  <div class="text-sm font-medium mb-2">
                                    {{ t('canvasUI.canvasBuilder.dataCharts.configureArrayObjectFields') }}
                                  </div>
                                  <div class="flex flex-col gap-2">
                                    <div>
                                      <label class="block mb-1 text-sm">
                                        {{ t('canvasUI.canvasBuilder.dataCharts.labelField') }}
                                      </label>
                                      <NSelect
                                        v-model:value="point.arrayConfig.labelField"
                                        :options="getFieldOptions(point.selectedValue)"
                                        @update:value="updateDynamicValueExpression(point)"
                                      />
                                    </div>
                                    <div>
                                      <label class="block mb-1 text-sm">
                                        {{ t('canvasUI.canvasBuilder.dataCharts.valueField') }}
                                      </label>
                                      <NSelect
                                        v-model:value="point.arrayConfig.valueField"
                                        :options="getFieldOptions(point.selectedValue)"
                                        @update:value="updateDynamicValueExpression(point)"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </template>
                            </div>
                          </div>
                        </NPopover>
                      </template>
                    </NInput>
                    <CButton
                      circle
                      class="bg-error2 self-center"
                      size="small"
                      :style="deleteButtonStyle"
                      type="error"
                      @click="removeDataPoint(index)"
                    >
                      <template #icon>
                        <CIcon class="color-error" fa-type="far" icon="trash" :size="12" />
                      </template>
                    </CButton>
                  </div>
                </template>

                <!-- XY data points (label + x + y) for scatter charts, and (label + x + y + r) for bubble charts -->
                <template v-else>
                  <div v-for="(point, index) in activeSeries.dataPoints" :key="index" class="flex gap-2 mb-2">
                    <NInput v-model:value="point.label" class="flex-1" placeholder="Label" />
                    <NInput v-model:value="point.xValue" class="flex-1" placeholder="X Value" type="text">
                      <template v-slot:suffix>
                        <NPopover
                          placement="bottom"
                          :show="activePopoverPoint === point && activePopoverField === 'x'"
                          trigger="manual"
                          @clickoutside="closePopover"
                        >
                          <template #trigger>
                            <div class="cursor-pointer flex items-center" @click="handleDynamicValueClick(point, 'x')">
                              <CIcon :class="point.dynamicXValue ? 'text-blue-500' : ''" icon="caret-down" />
                            </div>
                          </template>
                          <div class="p-4 min-w-[400px] max-h-[400px]">
                            <div class="flex gap-4 h-full">
                              <div class="flex-1 flex flex-col">
                                <div class="text-sm mb-2">
                                  {{ t('canvasUI.canvasBuilder.dataCharts.selectDynamicValue') }}
                                </div>
                                <div class="overflow-auto">
                                  <NTree
                                    block-line
                                    class="max-h-[300px]"
                                    :data="dynamicValueTreeData"
                                    :render-label="renderDynamicValueTreeLabel"
                                    :selected-keys="[point.dynamicXValue?.path ?? '']"
                                    @update:selected-keys="(keys) => handleDynamicValueSelect(keys, point, 'x')"
                                  />
                                </div>
                              </div>
                              <!-- Field Selection for Arrays/Objects (XY mode) -->
                              <template
                                v-if="
                                  point.selectedXValue && point.arrayConfig && isArrayOrObject(point.selectedXValue)
                                "
                              >
                                <div class="flex-1 border-l pl-4">
                                  <div class="text-sm font-medium mb-2">
                                    {{ t('canvasUI.canvasBuilder.dataCharts.configureArrayObjectFields') }}
                                  </div>
                                  <div class="flex flex-col gap-2">
                                    <div>
                                      <label class="block mb-1 text-sm">
                                        {{ t('canvasUI.canvasBuilder.dataCharts.labelField') }}
                                      </label>
                                      <NSelect
                                        v-model:value="point.arrayConfig.labelField"
                                        :options="getFieldOptions(point.selectedXValue)"
                                        @update:value="updateXYDynamicValueExpression(point)"
                                      />
                                    </div>
                                    <div>
                                      <label class="block mb-1 text-sm">
                                        {{ t('canvasUI.canvasBuilder.dataCharts.xValueField') }}
                                      </label>
                                      <NSelect
                                        v-model:value="point.arrayConfig.xField"
                                        :options="getFieldOptions(point.selectedXValue)"
                                        @update:value="updateXYDynamicValueExpression(point)"
                                      />
                                    </div>
                                    <div>
                                      <label class="block mb-1 text-sm">
                                        {{ t('canvasUI.canvasBuilder.dataCharts.yValueField') }}
                                      </label>
                                      <NSelect
                                        v-model:value="point.arrayConfig.yField"
                                        :options="getFieldOptions(point.selectedXValue)"
                                        @update:value="updateXYDynamicValueExpression(point)"
                                      />
                                    </div>
                                    <!-- R Value Field for bubble charts -->
                                    <div v-if="isActiveSeriesXYRChart">
                                      <label class="block mb-1 text-sm">
                                        {{ t('canvasUI.canvasBuilder.dataCharts.rValueField') }}
                                      </label>
                                      <NSelect
                                        v-model:value="point.arrayConfig.rField"
                                        :options="getFieldOptions(point.selectedXValue)"
                                        @update:value="updateXYDynamicValueExpression(point)"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </template>
                            </div>
                          </div>
                        </NPopover>
                      </template>
                    </NInput>
                    <NInput v-model:value="point.yValue" class="flex-1" placeholder="Y Value" type="text">
                      <template v-slot:suffix>
                        <NPopover
                          placement="bottom"
                          :show="activePopoverPoint === point && activePopoverField === 'y'"
                          trigger="manual"
                          @clickoutside="closePopover"
                        >
                          <template #trigger>
                            <div class="cursor-pointer flex items-center" @click="handleDynamicValueClick(point, 'y')">
                              <CIcon :class="point.dynamicYValue ? 'text-blue-500' : ''" icon="caret-down" />
                            </div>
                          </template>
                          <div class="p-4 min-w-[400px] max-h-[400px]">
                            <div class="flex gap-4 h-full">
                              <div class="flex-1 flex flex-col">
                                <div class="text-sm mb-2">
                                  {{ t('canvasUI.canvasBuilder.dataCharts.selectDynamicValue') }}
                                </div>
                                <div class="overflow-auto">
                                  <NTree
                                    block-line
                                    class="max-h-[300px]"
                                    :data="dynamicValueTreeData"
                                    :render-label="renderDynamicValueTreeLabel"
                                    :selected-keys="[point.dynamicYValue?.path ?? '']"
                                    @update:selected-keys="(keys) => handleDynamicValueSelect(keys, point, 'y')"
                                  />
                                </div>
                              </div>
                              <!-- Field Selection for Arrays/Objects (Y field) -->
                              <template
                                v-if="
                                  point.selectedYValue && point.arrayConfig && isArrayOrObject(point.selectedYValue)
                                "
                              >
                                <div class="flex-1 border-l pl-4">
                                  <div class="text-sm font-medium mb-2">
                                    {{ t('canvasUI.canvasBuilder.dataCharts.configureArrayObjectFields') }}
                                  </div>
                                  <div class="flex flex-col gap-2">
                                    <div>
                                      <label class="block mb-1 text-sm">
                                        {{ t('canvasUI.canvasBuilder.dataCharts.labelField') }}
                                      </label>
                                      <NSelect
                                        v-model:value="point.arrayConfig.labelField"
                                        :options="getFieldOptions(point.selectedYValue)"
                                        @update:value="updateXYDynamicValueExpression(point)"
                                      />
                                    </div>
                                    <div>
                                      <label class="block mb-1 text-sm">
                                        {{ t('canvasUI.canvasBuilder.dataCharts.xValueField') }}
                                      </label>
                                      <NSelect
                                        v-model:value="point.arrayConfig.xField"
                                        :options="getFieldOptions(point.selectedYValue)"
                                        @update:value="updateXYDynamicValueExpression(point)"
                                      />
                                    </div>
                                    <div>
                                      <label class="block mb-1 text-sm">
                                        {{ t('canvasUI.canvasBuilder.dataCharts.yValueField') }}
                                      </label>
                                      <NSelect
                                        v-model:value="point.arrayConfig.yField"
                                        :options="getFieldOptions(point.selectedYValue)"
                                        @update:value="updateXYDynamicValueExpression(point)"
                                      />
                                    </div>
                                    <!-- R Value Field for bubble charts -->
                                    <div v-if="isActiveSeriesXYRChart">
                                      <label class="block mb-1 text-sm">
                                        {{ t('canvasUI.canvasBuilder.dataCharts.rValueField') }}
                                      </label>
                                      <NSelect
                                        v-model:value="point.arrayConfig.rField"
                                        :options="getFieldOptions(point.selectedYValue)"
                                        @update:value="updateXYDynamicValueExpression(point)"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </template>
                            </div>
                          </div>
                        </NPopover>
                      </template>
                    </NInput>
                    <!-- R Value input for bubble charts -->
                    <NInput
                      v-if="isActiveSeriesXYRChart"
                      v-model:value="point.rValue"
                      class="flex-1"
                      placeholder="Size (R)"
                      type="text"
                    >
                      <template v-slot:suffix>
                        <NPopover
                          placement="bottom"
                          :show="activePopoverPoint === point && activePopoverField === 'r'"
                          trigger="manual"
                          @clickoutside="closePopover"
                        >
                          <template #trigger>
                            <div class="cursor-pointer flex items-center" @click="handleDynamicValueClick(point, 'r')">
                              <CIcon :class="point.dynamicRValue ? 'text-blue-500' : ''" icon="caret-down" />
                            </div>
                          </template>
                          <div class="p-4 min-w-[400px] max-h-[400px]">
                            <div class="flex gap-4 h-full">
                              <div class="flex-1 flex flex-col">
                                <div class="text-sm mb-2">
                                  {{ t('canvasUI.canvasBuilder.dataCharts.selectDynamicValue') }}
                                </div>
                                <div class="overflow-auto">
                                  <NTree
                                    block-line
                                    class="max-h-[300px]"
                                    :data="dynamicValueTreeData"
                                    :render-label="renderDynamicValueTreeLabel"
                                    :selected-keys="[point.dynamicRValue?.path ?? '']"
                                    @update:selected-keys="(keys) => handleDynamicValueSelect(keys, point, 'r')"
                                  />
                                </div>
                              </div>
                              <!-- Field Selection for Arrays/Objects (R field) -->
                              <template
                                v-if="
                                  point.selectedRValue && point.arrayConfig && isArrayOrObject(point.selectedRValue)
                                "
                              >
                                <div class="flex-1 border-l pl-4">
                                  <div class="text-sm font-medium mb-2">
                                    {{ t('canvasUI.canvasBuilder.dataCharts.configureArrayObjectFields') }}
                                  </div>
                                  <div class="flex flex-col gap-2">
                                    <div>
                                      <label class="block mb-1 text-sm">
                                        {{ t('canvasUI.canvasBuilder.dataCharts.labelField') }}
                                      </label>
                                      <NSelect
                                        v-model:value="point.arrayConfig.labelField"
                                        :options="getFieldOptions(point.selectedRValue)"
                                        @update:value="updateXYDynamicValueExpression(point)"
                                      />
                                    </div>
                                    <div>
                                      <label class="block mb-1 text-sm">
                                        {{ t('canvasUI.canvasBuilder.dataCharts.xValueField') }}
                                      </label>
                                      <NSelect
                                        v-model:value="point.arrayConfig.xField"
                                        :options="getFieldOptions(point.selectedRValue)"
                                        @update:value="updateXYDynamicValueExpression(point)"
                                      />
                                    </div>
                                    <div>
                                      <label class="block mb-1 text-sm">
                                        {{ t('canvasUI.canvasBuilder.dataCharts.yValueField') }}
                                      </label>
                                      <NSelect
                                        v-model:value="point.arrayConfig.yField"
                                        :options="getFieldOptions(point.selectedRValue)"
                                        @update:value="updateXYDynamicValueExpression(point)"
                                      />
                                    </div>
                                    <!-- R Value Field for bubble charts -->
                                    <div>
                                      <label class="block mb-1 text-sm">
                                        {{ t('canvasUI.canvasBuilder.dataCharts.rValueField') }}
                                      </label>
                                      <NSelect
                                        v-model:value="point.arrayConfig.rField"
                                        :options="getFieldOptions(point.selectedRValue)"
                                        @update:value="updateXYDynamicValueExpression(point)"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </template>
                            </div>
                          </div>
                        </NPopover>
                      </template>
                    </NInput>
                    <CButton
                      circle
                      class="bg-error2 self-center"
                      size="small"
                      :style="deleteButtonStyle"
                      type="error"
                      @click="removeDataPoint(index)"
                    >
                      <template #icon>
                        <CIcon class="color-error" fa-type="far" icon="trash" :size="12" />
                      </template>
                    </CButton>
                  </div>
                </template>

                <div class="flex justify-center mt-2">
                  <CButton class="mt-2" icon-placement="left" size="small" @click="addDataPoint">
                    <template #icon>
                      <CIcon fa-type="fas" icon="plus" />
                    </template>
                    {{ t('canvasUI.common.add') }}
                  </CButton>
                </div>
              </div>

              <!-- Chart Settings Section -->
              <div
                class="text-sm font-semibold mb-3 mt-6 text-gray-600 dark:text-gray-400 uppercase tracking-wide border-t border-gray-200 dark:border-gray-700 pt-6"
              >
                {{ t('canvasUI.canvasBuilder.dataCharts.chartSettings') }}
              </div>

              <!-- Chart Title -->
              <div class="mb-4">
                <label class="block mb-2">{{ t('canvasUI.canvasBuilder.dataCharts.chartTitle') }}</label>
                <NInput v-model:value="chartTitle" type="text" />
              </div>

              <!-- Axis Labels (for bar, line charts) -->
              <template v-if="['bar', 'line'].includes(primaryChartType)">
                <div class="mb-4">
                  <label class="block mb-2">{{ t('canvasUI.canvasBuilder.dataCharts.xAxisLabel') }}</label>
                  <NInput v-model:value="xAxisLabel" type="text" />
                </div>
                <div class="mb-4">
                  <label class="block mb-2">{{ t('canvasUI.canvasBuilder.dataCharts.yAxisLabel') }}</label>
                  <NInput v-model:value="yAxisLabel" type="text" />
                </div>
                <div class="mb-4">
                  <label class="block mb-2">{{ t('canvasUI.canvasBuilder.dataCharts.orientation') }}</label>
                  <NSelect v-model:value="orientation" :options="orientationOptions" />
                </div>
              </template>

              <!-- Legend Position (only for standalone charts) -->
              <div v-if="['pie', 'doughnut', 'polarArea'].includes(primaryChartType)" class="mb-4">
                <label class="block mb-2">{{ t('canvasUI.canvasBuilder.dataCharts.legendPosition') }}</label>
                <NSelect v-model:value="legendPosition" :options="legendPositionOptions" />
              </div>

              <!-- Point Style (for line, scatter, area charts) -->
              <div v-if="['line', 'scatter', 'area'].includes(primaryChartType)" class="mb-4">
                <label class="block mb-2">{{ t('canvasUI.canvasBuilder.dataCharts.pointStyle') }}</label>
                <NSelect v-model:value="pointStyle" :options="pointStyleOptions" />
              </div>

              <!-- Color Scheme -->
              <div class="mb-4">
                <label class="block mb-2">{{ t('canvasUI.canvasBuilder.dataCharts.colorScheme') }}</label>
                <NSelect
                  v-model:value="colorSchemeType"
                  class="mb-2"
                  :options="[
                    { label: t('canvasUI.canvasBuilder.dataCharts.useThemeColors'), value: 'theme' },
                    { label: t('canvasUI.canvasBuilder.dataCharts.useCustomColors'), value: 'custom' },
                  ]"
                />
                <CPaletteConfigurator
                  :key="colorSchemeType"
                  :allow-add="colorSchemeType === 'custom'"
                  :allow-delete="colorSchemeType === 'custom'"
                  :allow-edit="colorSchemeType === 'custom'"
                  :allow-order="true"
                  :initial-palette="displayColors"
                  @update:palette="updateChartColors"
                />
              </div>

              <!-- Animation -->
              <div class="mb-4">
                <label class="block mb-2">{{ t('canvasUI.canvasBuilder.dataCharts.animation') }}</label>
                <NSwitch v-model:value="enableAnimation" />
              </div>

              <!-- Percent Display -->
              <div class="mb-4">
                <label class="block mb-2">{{ t('canvasUI.canvasBuilder.dataCharts.percentDisplay') }}</label>
                <div class="flex items-center gap-2">
                  <NSwitch v-model:value="percentDisplay" />
                  <span class="text-sm text-gray-500">
                    {{ t('canvasUI.canvasBuilder.dataCharts.percentDisplayHint') }}
                  </span>
                </div>
              </div>

              <!-- View Mode Controls -->
              <div class="mb-4">
                <label class="block mb-2">{{ t('canvasUI.canvasBuilder.dataCharts.viewModeControls') }}</label>
                <div class="flex flex-col gap-2">
                  <div class="flex items-center gap-2">
                    <NSwitch v-model:value="enableFullscreenBtn" />
                    <span class="text-sm">{{ t('canvasUI.canvasBuilder.dataCharts.enableFullscreenBtn') }}</span>
                  </div>
                  <NTooltip :disabled="!isMultiSeries" trigger="hover">
                    <template #trigger>
                      <div class="flex items-center gap-2" :class="{ 'opacity-50 cursor-not-allowed': isMultiSeries }">
                        <NSwitch v-model:value="enableChartTypeSwitcher" :disabled="isMultiSeries" />
                        <span class="text-sm">
                          {{ t('canvasUI.canvasBuilder.dataCharts.enableChartTypeSwitcher') }}
                        </span>
                      </div>
                    </template>
                    {{ t('canvasUI.canvasBuilder.dataCharts.chartTypeSwitcherMultiSeriesDisabled') }}
                  </NTooltip>
                  <div v-if="isAdminMode" class="flex items-center gap-2">
                    <NSwitch v-model:value="enableDataEntry" />
                    <span class="text-sm">{{ t('canvasUI.canvasBuilder.dataCharts.enableDataEntry') }}</span>
                  </div>
                </div>
              </div>

              <!-- Chart Size -->
              <div class="mb-4">
                <label class="block mb-2">{{ t('canvasUI.canvasBuilder.dataCharts.chartSize') }}</label>
                <div class="flex flex-col gap-3">
                  <div class="flex items-center gap-2">
                    <label class="text-sm w-16">{{ t('canvasUI.canvasBuilder.dataCharts.height') }}</label>
                    <NInputNumber v-model:value="chartHeight" class="flex-1" :max="1000" :min="100" :step="10">
                      <template #suffix>px</template>
                    </NInputNumber>
                  </div>
                  <div class="flex items-center gap-2">
                    <label class="text-sm w-16">{{ t('canvasUI.canvasBuilder.dataCharts.width') }}</label>
                    <NInputNumber v-model:value="chartWidth" class="flex-1" :max="100" :min="10" :step="5">
                      <template #suffix>%</template>
                    </NInputNumber>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Preview Panel -->
        <div class="cb-data-charts-settings__preview flex-1 border-l border-[#eee] pl-4">
          <div class="text-sm font-medium mb-4">{{ t('canvasUI.canvasBuilder.dataCharts.preview') }}</div>
          <div class="preview-container" style="height: 300px">
            <NCard :bordered="false">
              <DataChartsRaw
                :key="JSON.stringify(previewData)"
                :data="{
                  ...previewData,
                  context: crmShape,
                }"
                is-preview
              />
            </NCard>
          </div>
        </div>
      </div>
    </NCard>
    <template #footer>
      <div class="flex justify-between gap-4">
        <CButton @click="onCancel">
          {{ t('canvasUI.common.cancel') }}
        </CButton>
        <CButton type="primary" @click="onSave">
          {{ t('canvasUI.common.save') }}
        </CButton>
      </div>
    </template>
  </NModal>
</template>

<script setup lang="ts">
import type {
  ChartType,
  DataPoint,
  ExtendedChartConfiguration,
  Series,
} from '@canvas-builder/components/ui/DataCharts/types'
import {
  ONE_D_CHART_TYPES,
  STANDALONE_CHART_TYPES,
  THREE_D_CHART_TYPES,
  TWO_D_CHART_TYPES,
  XY_CHART_TYPES,
  XYR_CHART_TYPES,
} from '@canvas-builder/components/ui/DataCharts/types'
import { CanvasBuilderMode } from '@canvas-builder/types/canvas'
import {
  NButton,
  NCard,
  NInput,
  NInputNumber,
  NModal,
  NPopover,
  NSelect,
  NSwitch,
  NTooltip,
  NTree,
  type TreeOption,
} from 'naive-ui'
import { computed, h, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasTheme from '@canvas-builder/composables/useCanvasTheme'
import useCrmShape from '@canvas-builder/composables/useCrmShape'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CPaletteConfigurator from '@lib/components/CPaletteConfigurator/CPaletteConfigurator.vue'
import useConfirmation from '@lib/composables/useConfirmation'
import useThemeVars from '@lib/composables/useThemeVars'
import DataChartsRaw from './DataCharts.raw.vue'

const props = defineProps<{
  id: string
  data: ExtendedChartConfiguration | null
}>()

const { t } = useI18n()
const themeVars = useThemeVars()
const { mode, setComponentEditMode, updateNodeDataById } = useCanvas()
const isAdminMode = computed(() => mode.value === CanvasBuilderMode.ADMIN)
const confirmation = useConfirmation()
const { crmShape } = useCrmShape()
const { palette } = useCanvasTheme()

// Helper to generate unique series ID
let seriesIdCounter = 0
function generateSeriesId(): string {
  return `series_${Date.now()}_${seriesIdCounter++}`
}

// Helper to convert saved series data to runtime format
function convertSavedSeriesToRuntime(savedSeries: any): Series {
  return {
    id: savedSeries.id || generateSeriesId(),
    name: savedSeries.name || 'Series 1',
    chartType: (savedSeries.chart_type || 'bar') as ChartType,
    dataPoints: (savedSeries.data_points || []).map((dp: any) => ({
      label: dp.label || '',
      value: dp.value || '',
      xValue: dp.x_value ?? '',
      yValue: dp.y_value ?? '',
      rValue: dp.r_value ?? '',
      dynamicValue: dp.dynamic_value,
      dynamicXValue: dp.dynamic_x_value,
      dynamicYValue: dp.dynamic_y_value,
      dynamicRValue: dp.dynamic_r_value,
      arrayConfig: dp.array_config,
    })),
  }
}

// Helper to convert legacy single-series data to new format
function convertLegacyToSeries(): Series[] {
  const chartTypeValue = (props.data?.type ?? props.data?._primary_chart_type ?? 'bar') as ChartType
  const dataPoints: DataPoint[] =
    props.data?.data_points && Array.isArray(props.data.data_points)
      ? props.data.data_points.map((dp) => ({
          label: dp.label,
          value: dp.value,
          xValue: dp.x_value ?? '',
          yValue: dp.y_value ?? '',
          dynamicValue: dp.dynamic_value,
          dynamicXValue: dp.dynamic_x_value,
          dynamicYValue: dp.dynamic_y_value,
          arrayConfig: dp.array_config,
        }))
      : props.data?.data?.labels
        ? (props.data.data.labels as string[]).map((label: string, index: number) => ({
            label: String(label),
            value: String(props.data?.data?.datasets?.[0]?.data?.[index] ?? 0),
            xValue: '',
            yValue: '',
          }))
        : []

  return [
    {
      id: generateSeriesId(),
      name: t('canvasUI.canvasBuilder.dataCharts.series') + ' 1',
      chartType: chartTypeValue,
      dataPoints,
    },
  ]
}

// Initialize series list
const seriesList = ref<Series[]>(
  props.data?.series && Array.isArray(props.data.series) && props.data.series.length > 0
    ? props.data.series.map((s) => convertSavedSeriesToRuntime(s))
    : convertLegacyToSeries(),
)

// Active series index
const activeSeriesIndex = ref(0)

// Computed active series
const activeSeries = computed(() => seriesList.value[activeSeriesIndex.value])

// Check if active series is XY chart (scatter)
const isActiveSeriesXYChart = computed(() => XY_CHART_TYPES.includes(activeSeries.value?.chartType))

// Check if active series is XYR chart (bubble - needs x, y, and r values)
const isActiveSeriesXYRChart = computed(() => XYR_CHART_TYPES.includes(activeSeries.value?.chartType))

// Check if primary (first) series has standalone chart type
const isPrimaryStandalone = computed(() => STANDALONE_CHART_TYPES.includes(seriesList.value[0]?.chartType))

// Check if we have multiple series
const isMultiSeries = computed(() => seriesList.value.length > 1)

// Check if we can add more series
const canAddSeries = computed(() => !isPrimaryStandalone.value)

// Get available chart type options based on current state
const availableChartTypeOptions = computed(() => {
  const allOptions = [
    { label: t('canvasUI.canvasBuilder.dataCharts.types.bar'), value: 'bar' },
    { label: t('canvasUI.canvasBuilder.dataCharts.types.line'), value: 'line' },
    { label: t('canvasUI.canvasBuilder.dataCharts.types.pie'), value: 'pie' },
    { label: t('canvasUI.canvasBuilder.dataCharts.types.doughnut'), value: 'doughnut' },
    { label: t('canvasUI.canvasBuilder.dataCharts.types.polarArea'), value: 'polarArea' },
    { label: t('canvasUI.canvasBuilder.dataCharts.types.radar'), value: 'radar' },
    { label: t('canvasUI.canvasBuilder.dataCharts.types.scatter'), value: 'scatter' },
    { label: t('canvasUI.canvasBuilder.dataCharts.types.bubble'), value: 'bubble' },
    { label: t('canvasUI.canvasBuilder.dataCharts.types.area'), value: 'area' },
  ]

  const primaryType = seriesList.value[0]?.chartType

  // For multi-series charts, we need to ensure data structure compatibility:
  // - 1D charts (bar, line, area) use label/value → can mix with each other
  // - 2D charts (scatter) use x/y → can only be scatter
  // - 3D charts (bubble) use x/y/r → can only be bubble
  if (seriesList.value.length > 1 || activeSeriesIndex.value > 0) {
    // Determine which category the primary chart belongs to
    if (THREE_D_CHART_TYPES.includes(primaryType as ChartType)) {
      // Bubble chart: only allow bubble for all series
      return allOptions.filter((opt) => THREE_D_CHART_TYPES.includes(opt.value as ChartType))
    }
    if (TWO_D_CHART_TYPES.includes(primaryType as ChartType)) {
      // Scatter chart: only allow scatter for all series
      return allOptions.filter((opt) => TWO_D_CHART_TYPES.includes(opt.value as ChartType))
    }
    // 1D charts: only allow 1D types (bar, line, area)
    return allOptions.filter((opt) => ONE_D_CHART_TYPES.includes(opt.value as ChartType))
  }

  // First series with no other series - show all options
  return allOptions
})

// Legacy compatibility - keep chartType as computed for existing code
const chartType = computed(() => seriesList.value[0]?.chartType ?? 'bar')

// Primary chart type (from first series) - used in template
const primaryChartType = computed(() => seriesList.value[0]?.chartType ?? 'bar')

// Chart configuration (global settings)
// Use saved color_scheme if available, fall back to datasets' background_color for backward compatibility
const savedColorScheme = props.data?.color_scheme
const datasets = props.data?.data?.datasets
const backgroundColor =
  savedColorScheme ??
  (datasets && datasets.length > 1
    ? datasets.map((ds: any) => ds.background_color).filter(Boolean)
    : datasets?.[0]?.background_color)
const colorSchemeType = ref<'theme' | 'custom'>(props.data?.color_scheme_type ?? 'theme')

const chartColors = ref<string[]>([])
const displayColors = computed(() => {
  if (colorSchemeType.value === 'theme') {
    return [...palette.value]
  }
  return chartColors.value
})

// Set initial colors
chartColors.value = (() => {
  if (Array.isArray(backgroundColor)) {
    if (colorSchemeType.value === 'theme') {
      return backgroundColor.map((color: string, i) => {
        if (color.startsWith('palette')) {
          return color.replace('palette', '')
        }
        const index = palette.value.findIndex((p: string) => p === color)
        return index >= 0 ? String(index) : String(i)
      })
    } else {
      return backgroundColor
    }
  }
  return colorSchemeType.value === 'theme'
    ? Array.from({ length: palette.value.length }, (_, i: number) => String(i))
    : [...palette.value]
})()

const chartTitle = ref(props.data?.options?.plugins?.title?.text ?? '')
const legendPosition = ref(props.data?.options?.plugins?.legend?.position ?? 'top')
const enableAnimation = ref((props.data?.options?.animation?.duration ?? 0) > 0)

// Add after existing refs
const xAxisLabel = ref(props.data?.options?.scales?.x?.title?.text ?? '')
const yAxisLabel = ref(props.data?.options?.scales?.y?.title?.text ?? '')
const orientation = ref(props.data?.options?.index_axis ?? 'x')
const pointStyle = ref(props.data?.data?.datasets?.[0]?.point_style ?? 'circle')

// New settings from custom app
const percentDisplay = ref(props.data?.percent_display ?? false)
const enableFullscreenBtn = ref(props.data?.view_controls?.enable_fullscreen_btn ?? false)
const enableChartTypeSwitcher = ref(props.data?.view_controls?.enable_chart_type_switcher ?? false)
const enableDataEntry = ref(props.data?.view_controls?.enable_data_entry !== false)

// Size settings
const chartHeight = ref(props.data?.chart_height ?? 800)
const chartWidth = ref(props.data?.chart_width ?? 100)

// Options (chartTypeOptions kept for backward compatibility, but availableChartTypeOptions is used in template)

const legendPositionOptions = [
  { label: t('canvasUI.canvasBuilder.dataCharts.legendPositions.none'), value: 'none' },
  { label: t('canvasUI.canvasBuilder.dataCharts.legendPositions.top'), value: 'top' },
  { label: t('canvasUI.canvasBuilder.dataCharts.legendPositions.bottom'), value: 'bottom' },
  { label: t('canvasUI.canvasBuilder.dataCharts.legendPositions.left'), value: 'left' },
  { label: t('canvasUI.canvasBuilder.dataCharts.legendPositions.right'), value: 'right' },
]

const orientationOptions = [
  { label: t('canvasUI.canvasBuilder.dataCharts.orientations.horizontal'), value: 'y' },
  { label: t('canvasUI.canvasBuilder.dataCharts.orientations.vertical'), value: 'x' },
]

const pointStyleOptions = [
  { label: t('canvasUI.canvasBuilder.dataCharts.pointStyles.circle'), value: 'circle' },
  { label: t('canvasUI.canvasBuilder.dataCharts.pointStyles.cross'), value: 'cross' },
  { label: t('canvasUI.canvasBuilder.dataCharts.pointStyles.dash'), value: 'dash' },
  { label: t('canvasUI.canvasBuilder.dataCharts.pointStyles.rect'), value: 'rect' },
  { label: t('canvasUI.canvasBuilder.dataCharts.pointStyles.star'), value: 'star' },
  { label: t('canvasUI.canvasBuilder.dataCharts.pointStyles.triangle'), value: 'triangle' },
]

const deleteButtonStyle = computed(() => ({
  '--n-border': '1px solid transparent',
  '--n-border-disabled': '1px solid transparent',
  '--n-border-focus': '1px solid transparent',
  '--n-border-hover': '1px solid transparent',
  '--n-border-pressed': '1px solid transparent',
  '--n-color-focus': themeVars.value.error2,
  '--n-color-hover': themeVars.value.error2,
  '--n-color-disabled': themeVars.value.error2,
  '--n-color-pressed': themeVars.value.error2,
}))

const activePopoverField = ref<'value' | 'x' | 'y' | 'r' | null>(null)

function updateChartColors(newColors: string[]) {
  if (colorSchemeType.value === 'theme') {
    // For theme mode, map the new order to indices
    chartColors.value = newColors.map((color, i) => {
      const index = palette.value.findIndex((p) => p === color)
      return String(index >= 0 ? index : i)
    })
  } else {
    // For custom mode, store the actual colors
    chartColors.value = [...newColors]
  }
  updatePreview()
}

watch(
  () => colorSchemeType.value,
  (newType) => {
    if (newType === 'theme') {
      // When switching to theme mode, preserve existing indices but ensure they're valid
      // using modulo for indices that are out of bounds
      chartColors.value = chartColors.value.map((index, i) => {
        const idx = parseInt(index)
        return String(idx >= 0 ? idx % palette.value.length : i)
      })
    } else {
      // When switching to custom mode, convert current theme indices to actual colors
      chartColors.value = chartColors.value.map((index) => {
        const idx = parseInt(index)
        return idx >= 0 ? palette.value[idx % palette.value.length] : palette.value[0]
      })
    }
  },
)

function closePopover() {
  activePopoverPoint.value = null
  activePopoverField.value = null
}

// Series management functions
function addSeries() {
  if (!canAddSeries.value) return

  const newIndex = seriesList.value.length
  const newSeries: Series = {
    id: generateSeriesId(),
    name: `${t('canvasUI.canvasBuilder.dataCharts.series')} ${newIndex + 1}`,
    chartType: seriesList.value[0].chartType, // Default to same type as first series
    dataPoints: [],
  }
  seriesList.value.push(newSeries)
  activeSeriesIndex.value = newIndex
  updatePreview()
}

function removeSeries(index: number) {
  if (index === 0 || index >= seriesList.value.length) return

  seriesList.value.splice(index, 1)
  // Adjust active index if needed
  if (activeSeriesIndex.value >= seriesList.value.length) {
    activeSeriesIndex.value = seriesList.value.length - 1
  }
  updatePreview()
}

function onChartTypeChange(newType: ChartType) {
  // If changing first series to standalone type, remove all other series
  if (activeSeriesIndex.value === 0 && STANDALONE_CHART_TYPES.includes(newType)) {
    seriesList.value = [seriesList.value[0]]
  }
  updatePreview()
}

function addDataPoint() {
  activeSeries.value.dataPoints.push({
    label: '',
    xValue: '',
    yValue: '',
    rValue: '',
    value: '',
  })
}

function removeDataPoint(index: number) {
  activeSeries.value.dataPoints.splice(index, 1)
}

function removeAllDataPoints() {
  activeSeries.value.dataPoints = []
}

function isArrayOrObject(value: any): boolean {
  return Array.isArray(value) || (typeof value === 'object' && value !== null)
}

// Parse a path string into parts, handling array access correctly
// e.g., "items[0].price" -> ["items", "[0]", "price"]
function parsePathIntoParts(path: string): string[] {
  const parts: string[] = []
  let current = ''

  for (let i = 0; i < path.length; i++) {
    const char = path[i]
    if (char === '.') {
      if (current) {
        parts.push(current)
        current = ''
      }
    } else if (char === '[') {
      if (current) {
        parts.push(current)
        current = ''
      }
      // Find the closing bracket
      const closingIdx = path.indexOf(']', i)
      if (closingIdx !== -1) {
        parts.push(path.substring(i, closingIdx + 1)) // Include brackets
        i = closingIdx
      }
    } else {
      current += char
    }
  }

  if (current) {
    parts.push(current)
  }

  return parts
}

// Convert bracket notation to dot notation for Handlebars
// "Dogs[0].age" -> "Dogs.0.age"
function convertBracketsToDotsPath(path: string): string {
  if (!path) return ''
  if (!path.includes('[')) return path

  // Convert array bracket notation to dot notation
  const convertedPath = path.replace(/\[(\d+)\]/g, '.$1')
  // Remove leading dot if the path started with bracket (unlikely but safe)
  return convertedPath.startsWith('.') ? convertedPath.slice(1) : convertedPath
}

// Generate a proper Handlebars expression for a path
// Converts paths like "Dogs[0].age" to Handlebars-compatible format
function generateHandlebarsExpression(path: string): string {
  if (!path) return ''

  const cleanPath = convertBracketsToDotsPath(path)
  return `{{${cleanPath}}}`
}

function getFieldOptions(data: any) {
  if (!data || typeof data !== 'object') return []

  const firstItem = Array.isArray(data) ? data[0] : data
  if (!firstItem || typeof firstItem !== 'object') return []

  return Object.keys(firstItem)
    .filter((key) => key !== '[[Prototype]]')
    .map((field) => ({
      label: field,
      value: field,
    }))
}

function updateDynamicValueExpression(point: DataPoint) {
  if (!point.selectedValue || !point.arrayConfig?.labelField || !point.arrayConfig?.valueField) {
    return
  }

  const path = point.dynamicValue?.path
  if (!path) {
    return
  }

  const isArray = Array.isArray(point.selectedValue)

  if (isArray) {
    // Create foreach expressions for arrays
    point.label = `{{#each ${path}}}{{lookup this "${point.arrayConfig.labelField}"}}{{#unless @last}},{{/unless}}{{/each}}`
    point.value = `{{#each ${path}}}{{lookup this "${point.arrayConfig.valueField}"}}{{#unless @last}},{{/unless}}{{/each}}`
  } else {
    // For objects, access the fields directly using dot notation
    // Convert bracket notation to dots for Handlebars compatibility
    const cleanPath = convertBracketsToDotsPath(path)
    point.label = `{{${cleanPath}.${point.arrayConfig.labelField}}}`
    point.value = `{{${cleanPath}.${point.arrayConfig.valueField}}}`
  }

  // Store the dynamic value configuration
  point.dynamicValue = {
    path,
    value: point.value,
  }

  // Close the popover when both fields are selected
  closePopover()
}

function updateXYDynamicValueExpression(point: DataPoint) {
  // Basic validation - need label, x, and y fields
  if (!point.arrayConfig?.labelField || !point.arrayConfig?.xField || !point.arrayConfig?.yField) {
    return
  }

  // For bubble charts, also require rField
  const isBubble = isActiveSeriesXYRChart.value
  if (isBubble && !point.arrayConfig?.rField) {
    return
  }

  // Determine which field was used to select the array/object
  // Check which selectedValue is actually an array or object (the one we're configuring)
  let selectedValue: any = null
  let path: string | undefined = undefined

  if (point.selectedXValue && isArrayOrObject(point.selectedXValue)) {
    selectedValue = point.selectedXValue
    path = point.dynamicXValue?.path
  } else if (point.selectedYValue && isArrayOrObject(point.selectedYValue)) {
    selectedValue = point.selectedYValue
    path = point.dynamicYValue?.path
  } else if (point.selectedRValue && isArrayOrObject(point.selectedRValue)) {
    selectedValue = point.selectedRValue
    path = point.dynamicRValue?.path
  }

  if (!selectedValue || !path) {
    return
  }

  const isArray = Array.isArray(selectedValue)

  if (isArray) {
    // Create foreach expressions for arrays
    point.label = `{{#each ${path}}}{{lookup this "${point.arrayConfig.labelField}"}}{{#unless @last}},{{/unless}}{{/each}}`
    point.xValue = `{{#each ${path}}}{{lookup this "${point.arrayConfig.xField}"}}{{#unless @last}},{{/unless}}{{/each}}`
    point.yValue = `{{#each ${path}}}{{lookup this "${point.arrayConfig.yField}"}}{{#unless @last}},{{/unless}}{{/each}}`

    // Handle rField for bubble charts
    if (isBubble && point.arrayConfig.rField) {
      point.rValue = `{{#each ${path}}}{{lookup this "${point.arrayConfig.rField}"}}{{#unless @last}},{{/unless}}{{/each}}`
      point.dynamicRValue = {
        path,
        value: point.rValue,
      }
    }
  } else {
    // For objects, access the fields directly using dot notation
    // Convert bracket notation to dots for Handlebars compatibility
    const cleanPath = convertBracketsToDotsPath(path)
    point.label = `{{${cleanPath}.${point.arrayConfig.labelField}}}`
    point.xValue = `{{${cleanPath}.${point.arrayConfig.xField}}}`
    point.yValue = `{{${cleanPath}.${point.arrayConfig.yField}}}`

    // Handle rField for bubble charts
    if (isBubble && point.arrayConfig.rField) {
      point.rValue = `{{${cleanPath}.${point.arrayConfig.rField}}}`
      point.dynamicRValue = {
        path,
        value: point.rValue,
      }
    }
  }

  // Store the dynamic value configurations
  point.dynamicXValue = {
    path,
    value: point.xValue,
  }
  point.dynamicYValue = {
    path,
    value: point.yValue,
  }

  // Close the popover when all fields are selected
  closePopover()
}

const dynamicValueTreeData = computed(() => {
  // Build path string from path parts, handling array indices correctly
  function buildPathString(pathParts: string[]): string {
    return pathParts.reduce((acc, part, index) => {
      if (part.startsWith('[')) {
        // Array index - no dot before it
        return acc + part
      }
      // Regular key - add dot separator (except for first part)
      return index === 0 ? part : `${acc}.${part}`
    }, '')
  }

  function processObject(obj: any, pathParts: string[] = []): any[] {
    const result: any[] = []

    for (const key in obj) {
      if (key === '[[Prototype]]') continue

      const value = obj[key]
      const currentPathParts = [...pathParts, key]
      const pathString = buildPathString(currentPathParts)

      if (Array.isArray(value)) {
        const arrayNode = {
          key: pathString,
          label: key,
          children: value.map((item, index) => {
            const arrayItemPath = [...currentPathParts.slice(0, -1), `${key}[${index}]`]
            const arrayItemPathString = buildPathString(arrayItemPath)
            if (typeof item === 'object' && item !== null) {
              return {
                key: arrayItemPathString,
                label: `[${index}]`,
                children: processObject(item, arrayItemPath),
              }
            }
            return {
              key: arrayItemPathString,
              label: `[${index}]: ${item}`,
              isLeaf: true,
            }
          }),
        }
        result.push(arrayNode)
      } else if (typeof value === 'object' && value !== null) {
        result.push({
          key: pathString,
          label: key,
          children: processObject(value, currentPathParts),
        })
      } else {
        result.push({
          key: pathString,
          label: `${key}: ${value}`,
          isLeaf: true,
        })
      }
    }

    return result
  }

  return processObject(crmShape.value ?? {})
})

function renderDynamicValueTreeLabel(info: { option: TreeOption }) {
  return h('span', { class: 'text-sm' }, { default: () => info.option.label || '' })
}

// Add this after other refs
const activePopoverPoint = ref<DataPoint | null>(null)

function handleDynamicValueClick(point: DataPoint, field: 'value' | 'x' | 'y' | 'r' = 'value') {
  // Toggle the popover
  if (activePopoverPoint.value === point && activePopoverField.value === field) {
    closePopover()
  } else {
    // Reset only the selected field's dynamic state when opening the popover
    // Don't clear other fields or manually entered values
    if (field === 'value') {
      point.selectedValue = undefined
      point.dynamicValue = undefined
      point.arrayConfig = undefined
    } else if (field === 'x') {
      point.selectedXValue = undefined
      point.dynamicXValue = undefined
      // Only clear arrayConfig if opening X - this affects all XY fields when selecting array/object
      point.arrayConfig = undefined
    } else if (field === 'y') {
      point.selectedYValue = undefined
      point.dynamicYValue = undefined
      point.arrayConfig = undefined
    } else if (field === 'r') {
      point.selectedRValue = undefined
      point.dynamicRValue = undefined
      point.arrayConfig = undefined
    }
    activePopoverPoint.value = point
    activePopoverField.value = field
  }
}

function handleDynamicValueSelect(keys: string[], point: DataPoint, field: 'value' | 'x' | 'y' | 'r' = 'value') {
  const selectedKey = keys[0]
  if (!selectedKey) {
    if (field === 'value') {
      point.dynamicValue = undefined
      point.selectedValue = undefined
      point.value = ''
    } else if (field === 'x') {
      point.dynamicXValue = undefined
      point.selectedXValue = undefined
      point.xValue = ''
    } else if (field === 'y') {
      point.dynamicYValue = undefined
      point.selectedYValue = undefined
      point.yValue = ''
    } else if (field === 'r') {
      point.dynamicRValue = undefined
      point.selectedRValue = undefined
      point.rValue = ''
    }
    closePopover()
    return
  }

  // Get the actual value at the selected path
  const pathParts = parsePathIntoParts(selectedKey)
  let currentValue: any = crmShape.value
  for (const part of pathParts) {
    if (!currentValue) break
    if (part.startsWith('[') && part.endsWith(']')) {
      // Array index access like "[0]"
      const index = parseInt(part.slice(1, -1))
      currentValue = currentValue?.[index]
    } else {
      // Property access
      currentValue = currentValue?.[part]
    }
  }

  // For XY fields (scatter/bubble charts)
  if (field === 'x') {
    point.selectedXValue = currentValue

    // If it's an array or object, initialize arrayConfig for field selection
    if (Array.isArray(currentValue) || (typeof currentValue === 'object' && currentValue !== null)) {
      point.arrayConfig = {
        labelField: '',
        valueField: '',
        xField: '',
        yField: '',
        rField: '',
      }
      point.dynamicXValue = {
        path: selectedKey,
        value: '',
      }
      // Don't close popover - wait for field selection
      return
    }

    // For simple values, use the path directly
    const expression = generateHandlebarsExpression(selectedKey)
    point.dynamicXValue = { path: selectedKey, value: expression }
    point.xValue = expression
    closePopover()
    return
  }

  if (field === 'y') {
    point.selectedYValue = currentValue

    // If it's an array or object, initialize arrayConfig for field selection
    if (Array.isArray(currentValue) || (typeof currentValue === 'object' && currentValue !== null)) {
      point.arrayConfig = {
        labelField: '',
        valueField: '',
        xField: '',
        yField: '',
        rField: '',
      }
      point.dynamicYValue = {
        path: selectedKey,
        value: '',
      }
      // Don't close popover - wait for field selection
      return
    }

    // For simple values, use the path directly
    const expression = generateHandlebarsExpression(selectedKey)
    point.dynamicYValue = { path: selectedKey, value: expression }
    point.yValue = expression
    closePopover()
    return
  }

  if (field === 'r') {
    point.selectedRValue = currentValue

    // If it's an array or object, initialize arrayConfig for field selection
    if (Array.isArray(currentValue) || (typeof currentValue === 'object' && currentValue !== null)) {
      point.arrayConfig = {
        labelField: '',
        valueField: '',
        xField: '',
        yField: '',
        rField: '',
      }
      point.dynamicRValue = {
        path: selectedKey,
        value: '',
      }
      // Don't close popover - wait for field selection
      return
    }

    // For simple values, use the path directly
    const expression = generateHandlebarsExpression(selectedKey)
    point.dynamicRValue = { path: selectedKey, value: expression }
    point.rValue = expression
    closePopover()
    return
  }

  point.selectedValue = currentValue

  // Initialize array config if it's an array or object
  if (Array.isArray(currentValue) || (typeof currentValue === 'object' && currentValue !== null)) {
    point.arrayConfig = {
      labelField: '',
      valueField: '',
    }
    point.dynamicValue = {
      path: selectedKey,
      value: '',
    }
  } else {
    // For simple values, use the path directly
    const expression = generateHandlebarsExpression(selectedKey)
    point.dynamicValue = {
      path: selectedKey,
      value: expression,
    }
    point.value = expression
    closePopover()
  }
}

// Normalize R values for bubble charts to a reasonable display range
// Returns objects with both normalized r (for display) and originalR (for tooltips)
function normalizeRValues(
  values: Array<{ x: number; y: number; r: number }>,
  minRadius: number = 5,
  maxRadius: number = 40,
): Array<{ x: number; y: number; r: number; originalR: number }> {
  if (values.length === 0) return []

  const rValues = values.map((v) => v.r)
  const minR = Math.min(...rValues)
  const maxR = Math.max(...rValues)

  // If all values are the same, use a middle radius
  if (minR === maxR) {
    return values.map((v) => ({
      ...v,
      r: (minRadius + maxRadius) / 2,
      originalR: v.r,
    }))
  }

  // Normalize to the range [minRadius, maxRadius], keeping original value
  return values.map((v) => ({
    ...v,
    r: minRadius + ((v.r - minR) / (maxR - minR)) * (maxRadius - minRadius),
    originalR: v.r,
  }))
}

function processSeriesData(series: Series) {
  const isXY = XY_CHART_TYPES.includes(series.chartType)
  const isXYR = XYR_CHART_TYPES.includes(series.chartType)

  if (isXY || isXYR) {
    const processedData = {
      labels: [] as string[],
      values: [] as Array<{ x: number; y: number; r?: number; originalR?: number }>,
    }

    // For bubble charts, collect raw data first then normalize
    const rawBubbleData: Array<{ label: string; x: number; y: number; r: number }> = []

    series.dataPoints.forEach((point: DataPoint) => {
      const x = !isNaN(Number(point.xValue)) ? Number(point.xValue) : 0
      const y = !isNaN(Number(point.yValue)) ? Number(point.yValue) : 0

      if (series.chartType === 'bubble') {
        // Use rValue if provided, otherwise use a default
        const r = point.rValue && !isNaN(Number(point.rValue)) ? Math.abs(Number(point.rValue)) : 10
        rawBubbleData.push({ label: point.label, x, y, r })
      } else {
        processedData.labels.push(point.label)
        processedData.values.push({ x, y })
      }
    })

    // For bubble charts, normalize the R values
    if (series.chartType === 'bubble' && rawBubbleData.length > 0) {
      const bubbleValues = rawBubbleData.map((d) => ({ x: d.x, y: d.y, r: d.r }))
      const normalizedValues = normalizeRValues(bubbleValues)

      rawBubbleData.forEach((d, idx) => {
        processedData.labels.push(d.label)
        processedData.values.push(normalizedValues[idx])
      })
    }

    return processedData
  }

  const processedData = {
    labels: [] as string[],
    values: [] as number[],
  }

  series.dataPoints.forEach((point: DataPoint) => {
    processedData.labels.push(point.label)
    processedData.values.push(!isNaN(Number(point.value)) ? Number(point.value) : 0)
  })

  return processedData
}

function getSeriesColors(forStorage: boolean = false) {
  if (colorSchemeType.value === 'theme') {
    if (forStorage) {
      // Return palette references for storage (will be resolved at render time)
      return chartColors.value.map((index) => `palette${index}`)
    }
    // Return actual colors for preview
    return chartColors.value.map((index) => {
      const idx = parseInt(index)
      return palette.value[idx % palette.value.length]
    })
  }
  return chartColors.value
}

function createChartConfig(): ExtendedChartConfiguration {
  // Build datasets for all series
  const datasets: any[] = []
  let allLabels: string[] = []
  const colorsForStorage = getSeriesColors(true) // Get palette refs for storage
  const isSingleSeries = seriesList.value.length === 1

  seriesList.value.forEach((series, index) => {
    // Process series data first to know how many data points we have
    const processedData = processSeriesData(series)

    // For single series: store the color scheme array (Chart.js will cycle through)
    // For multiple series: use one color per series
    let backgroundColor: string | string[]
    let borderColor: string | string[]

    if (isSingleSeries) {
      // Store the color scheme - Chart.js will automatically cycle through colors
      backgroundColor = colorsForStorage
      borderColor = [...colorsForStorage]
    } else {
      // Single color for the entire series
      const seriesColor = colorsForStorage[index % colorsForStorage.length]
      backgroundColor = seriesColor
      borderColor = seriesColor
    }

    const datasetOptions: Record<string, any> = {
      label: series.name,
      data: processedData.values,
      background_color: backgroundColor,
      border_color: borderColor,
      borderWidth: 1,
    }

    // For mixed charts, set the type per dataset
    if (!isSingleSeries) {
      if (series.chartType === 'area') {
        datasetOptions.type = 'line'
        datasetOptions.fill = true
        datasetOptions.tension = 0.4
      } else {
        datasetOptions.type = series.chartType
      }
    }

    // Handle special chart types
    if (series.chartType === 'area') {
      datasetOptions.fill = true
      datasetOptions.tension = 0.4
    }

    // Add point style for line/scatter charts
    if (['line', 'scatter', 'area'].includes(series.chartType)) {
      datasetOptions.pointStyle = pointStyle.value
    }

    // Add tension for line charts
    if (['line', 'area'].includes(series.chartType)) {
      datasetOptions.tension = 0.4
    }

    // Collect labels (use the longest label array)
    if (processedData.labels.length > allLabels.length) {
      allLabels = processedData.labels
    }

    datasets.push(datasetOptions)
  })

  // Primary chart type (from first series)
  const primaryType = seriesList.value[0].chartType
  let actualPrimaryType = primaryType as string
  if (primaryType === 'area') {
    actualPrimaryType = 'line'
  }

  // Configure scales based on primary chart type
  let scales: any = undefined
  const chartTypesWithScales = ['bar', 'line', 'scatter', 'bubble', 'area']
  if (chartTypesWithScales.includes(primaryType)) {
    scales = {
      x: {
        title: {
          display: !!xAxisLabel.value,
          text: xAxisLabel.value,
        },
      },
      y: {
        title: {
          display: !!yAxisLabel.value,
          text: yAxisLabel.value,
        },
        begin_at_zero: true,
      },
    }
  } else if (['radar', 'polarArea'].includes(primaryType)) {
    scales = {
      r: {
        begin_at_zero: true,
        ticks: {
          step_size: 1,
        },
      },
    }
  }

  // Show legend when there are multiple series
  const showLegend = seriesList.value.length > 1 || legendPosition.value !== 'none'

  const config = {
    type: actualPrimaryType,
    color_scheme_type: colorSchemeType.value,
    color_scheme: colorsForStorage,
    percent_display: percentDisplay.value,
    chart_height: chartHeight.value,
    chart_width: chartWidth.value,
    view_controls: {
      enable_fullscreen_btn: enableFullscreenBtn.value,
      enable_chart_type_switcher: enableChartTypeSwitcher.value,
      enable_data_entry: enableDataEntry.value,
    },
    data: {
      labels: allLabels,
      datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      index_axis: ['bar', 'line'].includes(primaryType) ? orientation.value : undefined,
      scales,
      plugins: {
        title: {
          display: !!chartTitle.value,
          text: chartTitle.value,
        },
        legend: {
          display: showLegend,
          position: legendPosition.value !== 'none' ? legendPosition.value : 'top',
          labels: {
            usePointStyle: true,
          },
        },
      },
      animation: {
        duration: enableAnimation.value ? 1000 : 0,
      },
    },
    // Save series data for editing
    series: seriesList.value.map((series) => ({
      id: series.id,
      name: series.name,
      chart_type: series.chartType,
      data_points: series.dataPoints.map((point) => ({
        label: point.label,
        value: point.value,
        x_value: point.xValue,
        y_value: point.yValue,
        r_value: point.rValue,
        dynamic_value: point.dynamicValue,
        dynamic_x_value: point.dynamicXValue,
        dynamic_y_value: point.dynamicYValue,
        dynamic_r_value: point.dynamicRValue,
        array_config: point.arrayConfig,
      })),
    })),
    // Store the primary chart type for reference
    _primary_chart_type: primaryType,
  } as ExtendedChartConfiguration

  return config
}

function onCancel() {
  if (isDirty.value) {
    confirmation.warning({
      title: t('canvasUI.canvasBuilder.confirmationOnUnsavedChanges.title'),
      content: t('canvasUI.canvasBuilder.confirmationOnUnsavedChanges.content'),
      confirmText: t('canvasUI.canvasBuilder.confirmationOnUnsavedChanges.confirm'),
      exitText: t('canvasUI.canvasBuilder.confirmationOnUnsavedChanges.exit'),
      hasExit: true,
      onConfirm: () => {
        onSave()
      },
      onExit: () => {
        setComponentEditMode(false)
      },
    })
    return
  }
  setComponentEditMode(false)
}

function onSave() {
  const chartData = createChartConfig()
  updateNodeDataById(props.id, chartData)
  setComponentEditMode(false)
}

// Add previewData ref
const previewData = ref<ExtendedChartConfiguration>({})

// Track if user has made changes
const isDirty = ref(false)
function markDirty() {
  isDirty.value = true
}

function updatePreview() {
  const config = createChartConfig()
  previewData.value = {
    ...config,
    context: crmShape.value,
  }
}

// Modify the watch section to update legend position when chart type changes
watch(
  chartType,
  (newType) => {
    if (!['pie', 'doughnut', 'polarArea'].includes(newType as string)) {
      legendPosition.value = 'none'
    }
  },
  { immediate: true },
)

// Disable chart type switcher when multi-series
watch(isMultiSeries, (multiSeries) => {
  if (multiSeries) {
    enableChartTypeSwitcher.value = false
  }
})

// Update the watch array to track all relevant changes
let isInitialized = false
watch(
  [
    seriesList,
    chartColors,
    colorSchemeType,
    chartTitle,
    legendPosition,
    xAxisLabel,
    yAxisLabel,
    orientation,
    pointStyle,
    percentDisplay,
    enableAnimation,
    enableFullscreenBtn,
    enableChartTypeSwitcher,
    enableDataEntry,
    chartHeight,
    chartWidth,
  ],
  () => {
    updatePreview()
    if (isInitialized) {
      markDirty()
    }
  },
  { deep: true },
)

onMounted(() => {
  updatePreview()
  // Set initialized flag after a tick to ensure initial watch triggers are complete
  nextTick(() => {
    isInitialized = true
  })
})
</script>
